import { useCallback, useEffect, useRef, useState } from "react";
import type { DocPosition, PdfPosition, ProjectView } from "../lib/api";

const SAVE_DELAY = 500;
/** Scrolling and typing move your position constantly, so those saves wait for a longer pause. Leaving the page saves straight away. */
const POSITION_DELAY = 2000;

/**
 * The file you have open, your other tabs, which folders you've expanded and where you were in each
 * file and in the PDF. It's saved to your account, so the project reopens the same way on any device you sign in from.
 */
export function useProjectView(projectId: string) {
  const [openPath, setOpenPathState] = useState<string | null>(null);
  const [tabs, setTabsState] = useState<string[]>([]);
  const [expanded, setExpandedState] = useState<Record<string, boolean>>({});
  // The latest values, for callbacks that change both at once.
  const current = useRef({ openPath: null as string | null, tabs: [] as string[] });
  // Only the editor reads these, when it opens a file, so changing them doesn't re-render anything.
  const positions = useRef<Record<string, DocPosition>>({});
  const pdfAt = useRef<PdfPosition | null>(null);
  const unsaved = useRef<Partial<ProjectView>>({});
  const timer = useRef<number | undefined>(undefined);
  const restored = useRef(false);

  const flush = useCallback(
    (leaving = false) => {
      clearTimeout(timer.current);
      const body = unsaved.current;
      if (!Object.keys(body).length) return;
      unsaved.current = {};
      // keepalive lets the request finish while the page is going away.
      fetch(`/api/projects/${projectId}/view`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        credentials: "same-origin",
        keepalive: leaving,
      }).catch(() => {});
    },
    [projectId],
  );

  const save = useCallback(
    (patch: Partial<ProjectView>, delay = SAVE_DELAY) => {
      Object.assign(unsaved.current, patch);
      clearTimeout(timer.current);
      timer.current = window.setTimeout(flush, delay);
    },
    [flush],
  );

  useEffect(() => {
    const leave = () => flush(true);
    window.addEventListener("pagehide", leave);
    return () => {
      window.removeEventListener("pagehide", leave);
      leave();
    };
  }, [flush]);

  /** Apply what the server had, once, when the project loads. */
  const restore = useCallback(
    (saved: ProjectView | null) => {
      if (restored.current) return;
      restored.current = true;
      let view = saved;
      // This used to live in the browser; carry it over the first time.
      const openKey = `benleaf:open:${projectId}`;
      const treeKey = `benleaf:tree:${projectId}`;
      try {
        if (!view) {
          const open = localStorage.getItem(openKey);
          const tree = localStorage.getItem(treeKey);
          if (open !== null || tree !== null) {
            view = { openPath: open === null ? null : JSON.parse(open), expanded: tree === null ? {} : JSON.parse(tree) };
            save(view);
          }
        }
        localStorage.removeItem(openKey);
        localStorage.removeItem(treeKey);
      } catch {}
      if (!view) return;
      const tabs = view.openTabs ?? (view.openPath ? [view.openPath] : []);
      current.current = { openPath: view.openPath, tabs };
      positions.current = { ...view.positions, ...positions.current };
      pdfAt.current ??= view.pdf ?? null;
      setOpenPathState(view.openPath);
      setTabsState(tabs);
      setExpandedState(view.expanded ?? {});
    },
    [projectId, save],
  );

  const show = useCallback(
    (openPath: string | null, tabs: string[]) => {
      const was = current.current;
      current.current = { openPath, tabs };
      setOpenPathState(openPath);
      setTabsState(tabs);
      const patch: Partial<ProjectView> = {};
      if (openPath !== was.openPath) patch.openPath = openPath;
      if (tabs.join("\n") !== was.tabs.join("\n")) patch.openTabs = tabs;
      if (Object.keys(patch).length) save(patch);
    },
    [save],
  );

  /** Open a file, giving it a tab next to the current one if it doesn't have one yet. */
  const setOpenPath = useCallback(
    (path: string | null) => {
      const { openPath, tabs } = current.current;
      if (!path || tabs.includes(path)) return show(path, tabs);
      const i = openPath ? tabs.indexOf(openPath) : -1;
      const at = i < 0 ? tabs.length : i + 1;
      show(path, [...tabs.slice(0, at), path, ...tabs.slice(at)]);
    },
    [show],
  );

  /** Close tabs; if the open file goes, its neighbour (to the right, else the left) opens instead. */
  const closeTabs = useCallback(
    (paths: string[]) => {
      const { openPath, tabs } = current.current;
      const gone = new Set(paths);
      const next = tabs.filter((t) => !gone.has(t));
      if (!openPath || !gone.has(openPath)) return show(openPath, next);
      const i = tabs.indexOf(openPath);
      const after = tabs.slice(i + 1).find((t) => !gone.has(t));
      const before = tabs.slice(0, i).reverse().find((t) => !gone.has(t));
      show(after ?? before ?? null, next);
    },
    [show],
  );

  /** Reorder tabs, or swap paths after a rename. */
  const setTabs = useCallback(
    (next: string[], nextOpen?: string | null) => show(nextOpen === undefined ? current.current.openPath : nextOpen, next),
    [show],
  );

  const setExpanded = useCallback(
    (next: Record<string, boolean>) => {
      setExpandedState(next);
      save({ expanded: next });
    },
    [save],
  );

  /** Where you were in a file, if you've had it open before. */
  const positionOf = useCallback((docId: string): DocPosition | null => positions.current[docId] ?? null, []);

  /** Only the files whose position changed are sent; the server keeps the rest. */
  const setPosition = useCallback(
    (docId: string, at: DocPosition) => {
      positions.current[docId] = at;
      save({ positions: { ...unsaved.current.positions, [docId]: at } }, POSITION_DELAY);
    },
    [save],
  );

  /** Where you were in the PDF, if you've scrolled it. */
  const pdfPosition = useCallback(() => pdfAt.current, []);

  const setPdfPosition = useCallback(
    (at: PdfPosition) => {
      pdfAt.current = at;
      save({ pdf: at }, POSITION_DELAY);
    },
    [save],
  );

  return { openPath, setOpenPath, tabs, setTabs, closeTabs, expanded, setExpanded, positionOf, setPosition, pdfPosition, setPdfPosition, restore };
}
